/*
 * TICK-051 / TICK-142 — per-event slip-verification keys, in the rebuilt drawer.
 *
 * The classic console had this and `?ui=v2` had nothing, so an event provisioned in the new console
 * could not be given a verifier at all. It sits beside the owner token because the two are the same
 * errand: a superadmin is in this drawer to set an event up on behalf of someone who cannot.
 *
 * Three things are said differently here than in the panel this replaces, and each is a defect
 * rather than a preference:
 *
 * - **A revealed key was shown as the input's `placeholder`.** Placeholder text is greyed out,
 *   is not selectable, and vanishes on the first keystroke — so the one thing anyone reveals a key
 *   in order to do, copy it, was the one thing they could not do. `CredentialReveal` shows it as a
 *   value, offers copy, and takes it off the screen again by itself.
 * - **Clearing a key turns slip verification off for the event**, and the classic control for it
 *   was an `×` with a tooltip. It is confirmed here, and the confirmation says what stops.
 * - **`exhausted` was reported as a failure.** It is a working key whose quota is spent; reading it
 *   as "ไม่สำเร็จ ลองใหม่ภายหลัง" sends the reader to re-check the key rather than the account
 *   behind it. Each of the five statuses now says its own thing.
 */
import { useState } from 'react';
import { Alert, Button, Card, ConfirmDialog, CredentialReveal, Select, TextField } from '../..';
import type { VendorKeyCheckDto } from '../../../api';
import type { EventVendorKeysState, Vendor } from './useEventVendorKeys';
import { VENDOR_LABEL, useEventVendorKeys } from './useEventVendorKeys';

const VENDORS: readonly Vendor[] = ['easyslip', 'thunder'];

/**
 * What the server found when it tried the key, in the reader's terms rather than the vendor's.
 *
 * `hasOther` is not decoration. `ChainedSlipVerifier` falls through to the other vendor on `error`,
 * `quota_exceeded` and `pending`, so an exhausted key is the difference between "this event stops
 * verifying slips" and "the other provider carries it" — and saying the first when the second is
 * true would send someone to buy quota they do not need tonight.
 */
export function checkNote(
  check: VendorKeyCheckDto, hasOther: boolean,
): { tone: 'positive' | 'warning' | 'danger'; text: string } {
  const left = check.remaining === null ? '' : ` — เหลือ ${check.remaining.toLocaleString('th-TH')} ครั้ง`;
  const covered = hasOther
    ? 'ผู้ให้บริการอีกรายจะรับช่วงไปก่อน'
    : 'ไม่มีผู้ให้บริการรายอื่นรับช่วง สลิปจะต้องให้คนตรวจเอง';
  switch (check.status) {
    case 'ok':
      return { tone: 'positive', text: `ทดสอบกับผู้ให้บริการแล้ว ใช้งานได้${left}` };
    case 'low_quota':
      return { tone: 'warning', text: `ใช้งานได้ แต่โควตาใกล้หมด${left} — เติมก่อนวันงาน` };
    case 'exhausted':
      // Not a failure of the key. Saying so would send someone to re-paste a key that is fine.
      return {
        tone: hasOther ? 'warning' : 'danger',
        text: `key ถูกต้อง แต่โควตาของบัญชีนี้หมดแล้ว — ${covered}`,
      };
    case 'unconfigured':
      return { tone: 'danger', text: 'ผู้ให้บริการไม่รับ key นี้ — ตรวจว่าคัดลอกมาครบและเป็นของบัญชีที่ถูกต้อง' };
    default:
      return {
        tone: 'warning',
        text: `ติดต่อผู้ให้บริการไม่ได้ตอนนี้${check.rawMessage ? ` (${check.rawMessage})` : ''} — key ถูกบันทึกไว้แล้ว ทดสอบใหม่ได้ด้วยการบันทึกซ้ำ`,
      };
  }
}

export function EventVendorKeysCard({ state, eventName }: { state: EventVendorKeysState; eventName: string }) {
  const [drafts, setDrafts] = useState<Record<Vendor, string>>({ thunder: '', easyslip: '' });
  const [clearing, setClearing] = useState<Vendor | null>(null);

  const { summary, loading, loadError, error, busy, checks } = state;
  const previewOf = (v: Vendor) => (v === 'thunder' ? summary?.thunderPreview : summary?.easySlipPreview) ?? null;

  const save = async (vendor: Vendor) => {
    const key = drafts[vendor].trim();
    if (!key) return;
    if (await state.setKey(vendor, key)) setDrafts((d) => ({ ...d, [vendor]: '' }));
  };

  const confirmClear = async () => {
    if (!clearing) return;
    if (await state.clearKey(clearing)) setClearing(null);
  };

  return (
    <Card
      title="ผู้ให้บริการตรวจสลิป"
      sub="key ของอีเวนต์นี้เอง ใช้ตรวจสลิปโอนเงินของผู้ซื้ออัตโนมัติ"
    >
      {loading && <p className="jt-field-hint">กำลังโหลด…</p>}
      {loadError && <Alert tone="danger" title="โหลดไม่สำเร็จ">{loadError}</Alert>}
      {error && <Alert tone="danger" title="ทำรายการไม่สำเร็จ">{error}</Alert>}

      {summary && (
        <>
          {/*
            * Stated once, at the top, because it is the rule that makes an empty field consequential
            * rather than merely unset: there is no platform key behind these.
            */}
          {!summary.thunderPreview && !summary.easySlipPreview && (
            <Alert tone="warning" title="อีเวนต์นี้ยังไม่ตรวจสลิปอัตโนมัติเลย">
              ยังไม่มี key ของผู้ให้บริการรายไหน — สลิปทุกใบของอีเวนต์นี้ต้องให้คนตรวจเอง
              และแพลตฟอร์มไม่ได้ใช้ key กลางแทนให้
            </Alert>
          )}

          {VENDORS.map((vendor) => {
            const preview = previewOf(vendor);
            const check = checks[vendor];
            const note = check ? checkNote(check, previewOf(vendor === 'thunder' ? 'easyslip' : 'thunder') !== null) : null;
            return (
              /* Named so the block and its actions are one thing to point at, in CSS and in a test. */
              <div key={vendor} className="jt-vendor-key">
                {preview ? (
                  <CredentialReveal
                    label={`${VENDOR_LABEL[vendor]} API key`}
                    masked={preview}
                    reveal={() => state.reveal(vendor)}
                  />
                ) : (
                  <p className="jt-field-hint">
                    <strong>{VENDOR_LABEL[vendor]}</strong> — ยังไม่ได้ตั้ง key จึงปิดอยู่สำหรับอีเวนต์นี้
                  </p>
                )}

                <TextField
                  label={preview ? `เปลี่ยน ${VENDOR_LABEL[vendor]} API key` : `ตั้ง ${VENDOR_LABEL[vendor]} API key`}
                  value={drafts[vendor]}
                  onChange={(e) => setDrafts((d) => ({ ...d, [vendor]: e.target.value }))}
                  hint={preview ? 'บันทึกแล้วจะแทนที่ key เดิมทันที' : undefined}
                  autoComplete="off"
                  spellCheck={false}
                />

                <div className="jt-inline-actions">
                  <Button
                    size="sm"
                    disabled={busy === vendor || !drafts[vendor].trim()}
                    onClick={() => save(vendor)}
                  >
                    บันทึก {VENDOR_LABEL[vendor]} key
                  </Button>
                  {preview && (
                    <Button
                      size="sm"
                      variant="destructive"
                      disabled={busy === vendor}
                      onClick={() => setClearing(vendor)}
                    >
                      ล้าง key
                    </Button>
                  )}
                </div>

                {note && (
                  <Alert
                    tone={note.tone === 'positive' ? 'success' : note.tone}
                    title={`ผลการทดสอบ ${VENDOR_LABEL[vendor]}`}
                  >
                    {note.text}
                  </Alert>
                )}
              </div>
            );
          })}

          {/*
            * Only meaningful with two keys to choose between — with one, the primary is whichever
            * one exists, and offering the choice would suggest a fallback that is not there.
            */}
          {summary.thunderPreview && summary.easySlipPreview && (
            <Select
              label="ผู้ให้บริการหลัก"
              hint="อีกรายจะถูกใช้อัตโนมัติเมื่อรายหลักตอบไม่ได้หรือโควตาหมด"
              value={summary.primary}
              disabled={busy === 'primary'}
              onChange={(e) => state.setPrimary(e.target.value as Vendor)}
              options={[
                { value: 'easyslip', label: 'EasySlip' },
                { value: 'thunder', label: 'Thunder' },
              ]}
            />
          )}
        </>
      )}

      <ConfirmDialog
        open={clearing !== null}
        onClose={() => setClearing(null)}
        onConfirm={confirmClear}
        title={`ล้าง ${clearing ? VENDOR_LABEL[clearing] : ''} key ของ ${eventName}`}
        consequences={[
          `${clearing ? VENDOR_LABEL[clearing] : ''} จะถูกปิดสำหรับอีเวนต์นี้ทันที`,
          'แพลตฟอร์มไม่ได้ใช้ key กลางแทนให้ — ไม่มีตัวสำรอง',
          summary && ((clearing === 'thunder' && !summary.easySlipPreview) || (clearing === 'easyslip' && !summary.thunderPreview))
            ? 'ไม่เหลือผู้ให้บริการรายอื่น สลิปทุกใบจะต้องให้คนตรวจเอง'
            : 'ผู้ให้บริการอีกรายยังทำงานอยู่',
          'key เดิมจะดูย้อนหลังไม่ได้ ต้องนำมาใส่ใหม่ถ้าจะเปิดอีกครั้ง',
        ]}
        confirmLabel="ล้าง key"
        busy={busy === clearing}
      />
    </Card>
  );
}

/**
 * The card bound to one event, which is how the page hands it to the drawer.
 *
 * Mounted per event — `key` on the caller side is what drops one organizer's key state before the
 * next one's loads, rather than this component trying to tell them apart.
 */
export function EventVendorKeysDestination({ token, eventId, eventName, onChanged }: {
  token: string; eventId: string; eventName: string;
  /** Refreshes the listing behind the drawer, which draws a badge from this same state. */
  onChanged?: () => Promise<unknown> | void;
}) {
  const state = useEventVendorKeys(token, eventId, onChanged);
  return <EventVendorKeysCard state={state} eventName={eventName} />;
}
